<template>
  <v-dialog v-model="internalValue" max-width="640" scrollable persistent transition="dialog-bottom-transition"
    content-class="releases-dialog">
    <v-card class="releases-card rounded-xl">
      <!-- Encabezado con degradado moderno -->
      <v-card-title class="releases-header py-4 px-6 white--text d-flex align-center">

        <div>
          <div class="text-h6 font-weight-bold line-height-tight">
            Novedades del Sistema
          </div>
          <div class="caption white--text opacity-80 font-weight-medium">
            Versión {{ currentReleaseVersion }} • {{ currentReleaseDate }}
          </div>
        </div>
        <v-spacer />
        <v-btn icon dark small class="close-btn" @click="close">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Contenido del Changelog -->
      <v-card-text class="pa-6 releases-content">
        <!-- Banner de bienvenida -->
        <div class="welcome-banner mb-5 pa-4 rounded-lg d-flex align-center">
          <v-icon large color="primary" class="mr-3 opacity-80">
            mdi-rocket-launch-outline
          </v-icon>
          <div>
            <div class="text-subtitle-2 font-weight-bold grey--text text--darken-3">
              {{ currentReleaseTitle }}
            </div>
            <div class="caption grey--text text--darken-1 mt-1">
              {{ currentReleaseDesc }}
            </div>
          </div>
        </div>

        <!-- Lista de cambios de la versión actual -->
        <div class="text-subtitle-2 font-weight-bold grey--text text--darken-2 mb-3 d-flex align-center">
          <v-icon small color="grey darken-2" class="mr-2">mdi-format-list-checks</v-icon>
          Cambios en esta versión
        </div>

        <div v-if="currentChanges && currentChanges.length" class="changes-list mb-4">
          <div v-for="(change, idx) in currentChanges" :key="idx" class="change-item pa-3 mb-2 rounded-lg">
            <div class="d-flex align-center mb-1">
              <v-chip x-small label class="font-weight-bold mr-2 text-uppercase" :class="getTypeChipClass(change.type)">
                <v-icon left x-small>{{ getTypeIcon(change.type) }}</v-icon>
                {{ change.type }}
              </v-chip>
              <span v-if="change.title" class="text-body-2 font-weight-bold grey--text text--darken-3">
                {{ change.title }}
              </span>
            </div>
            <div class="text-caption grey--text text--darken-2 pl-1 change-desc">
              {{ change.description }}
            </div>
          </div>
        </div>

        <div v-else class="text-center py-6 grey--text">
          <v-icon color="grey lighten-1" large>mdi-information-outline</v-icon>
          <div class="caption mt-2">No se detallaron cambios específicos para esta versión.</div>
        </div>

        <!-- Historial de versiones anteriores (si existen) -->
        <div v-if="olderReleases.length" class="mt-6">
          <v-expansion-panels flat class="history-panels">
            <v-expansion-panel class="rounded-lg">
              <v-expansion-panel-header class="py-2 px-3 text-caption font-weight-bold grey--text text--darken-2">
                <div class="d-flex align-center">
                  <v-icon small color="grey" class="mr-2">mdi-history</v-icon>
                  Ver versiones anteriores ({{ olderReleases.length }})
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="px-0 pt-2">
                <div v-for="(oldRelease, rIdx) in olderReleases" :key="rIdx"
                  class="old-release-block mb-4 pb-3 border-bottom">
                  <div class="d-flex align-center justify-space-between mb-2">
                    <span class="text-subtitle-2 font-weight-bold primary--text">
                      v{{ oldRelease.version }}
                    </span>
                    <span class="caption grey--text">{{ oldRelease.date }}</span>
                  </div>
                  <div v-for="(c, cIdx) in normalizeChanges(oldRelease.changes)" :key="cIdx"
                    class="d-flex align-start mb-2 pl-2">
                    <v-icon x-small :color="getTypeColor(c.type)" class="mr-2 mt-1">
                      {{ getTypeIcon(c.type) }}
                    </v-icon>
                    <div>
                      <strong v-if="c.title" class="caption grey--text text--darken-3">
                        {{ c.title }}:
                      </strong>
                      <span class="caption grey--text text--darken-2">
                        {{ c.description }}
                      </span>
                    </div>
                  </div>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <!-- Acciones del Modal -->
      <v-card-actions class="pa-4 px-6 bg-footer d-flex align-center justify-space-between">
        <span class="caption grey--text text--darken-1 font-weight-medium">

        </span>
        <v-btn color="primary" depressed class="rounded-lg px-6 font-weight-bold text-none primary-btn-glow"
          @click="close">
          <v-icon left small>mdi-check</v-icon>
          ¡Entendido!
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import changelogData from "@/assets/changelog.json";

export default {
  name: "Releases",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    currentVersion: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      releases: changelogData || [],
    };
  },
  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    activeRelease() {
      if (!this.releases || !this.releases.length) return null;
      // Busca la versión que coincida con currentVersion
      if (this.currentVersion) {
        const found = this.releases.find(
          (r) => r.version === this.currentVersion
        );
        if (found) return found;
      }
      // O toma la más reciente (primera en la lista)
      return this.releases[0];
    },
    currentReleaseVersion() {
      return this.activeRelease?.version || this.currentVersion || "Actual";
    },
    currentReleaseDate() {
      return this.activeRelease?.date || "";
    },
    currentReleaseTitle() {
      return (
        this.activeRelease?.title ||
        `¡Bienvenido a la versión ${this.currentReleaseVersion}!`
      );
    },
    currentReleaseDesc() {
      return (
        this.activeRelease?.description ||
        "Conoce las novedades, mejoras y ajustes implementados en el sistema."
      );
    },
    currentChanges() {
      if (!this.activeRelease || !this.activeRelease.changes) return [];
      return this.normalizeChanges(this.activeRelease.changes);
    },
    olderReleases() {
      if (!this.releases || this.releases.length <= 1) return [];
      const currentVer = this.currentReleaseVersion;
      return this.releases.filter((r) => r.version !== currentVer);
    },
  },
  methods: {
    normalizeChanges(changes) {
      if (!Array.isArray(changes)) return [];
      return changes.map((c) => {
        if (typeof c === "string") {
          return {
            type: "Novedad",
            title: "",
            description: c,
          };
        }
        return {
          type: c.type || c.tag || "Novedad",
          title: c.title || "",
          description: c.description || c.text || c.desc || "",
        };
      });
    },
    getTypeColor(type) {
      const t = (type || "").toLowerCase();
      if (t.includes("nuevo") || t.includes("nueva") || t.includes("feature")) {
        return "success";
      }
      if (t.includes("mejora") || t.includes("update") || t.includes("optim")) {
        return "primary";
      }
      if (t.includes("error") || t.includes("correc") || t.includes("fix") || t.includes("bug")) {
        return "amber darken-2";
      }
      return "grey darken-1";
    },
    getTypeChipClass(type) {
      const t = (type || "").toLowerCase();
      if (t.includes("nuevo") || t.includes("nueva") || t.includes("feature")) {
        return "chip-success";
      }
      if (t.includes("mejora") || t.includes("update") || t.includes("optim")) {
        return "chip-info";
      }
      if (t.includes("error") || t.includes("correc") || t.includes("fix") || t.includes("bug")) {
        return "chip-warning";
      }
      return "chip-default";
    },
    getTypeIcon(type) {
      const t = (type || "").toLowerCase();
      if (t.includes("nuevo") || t.includes("nueva") || t.includes("feature")) {
        return "mdi-star-four-points";
      }
      if (t.includes("mejora") || t.includes("update") || t.includes("optim")) {
        return "mdi-arrow-up-bold-circle-outline";
      }
      if (t.includes("error") || t.includes("correc") || t.includes("fix") || t.includes("bug")) {
        return "mdi-shield-check-outline";
      }
      return "mdi-check-circle-outline";
    },
    close() {
      this.internalValue = false;
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.releases-dialog {
  overflow: hidden;
}

.releases-card {
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2) !important;
  background-color: #ffffff;
  overflow: hidden;
}

.releases-header {
  background: linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%);
  position: relative;
}

.header-icon-avatar {
  background: rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(8px);
}

.close-btn {
  background: rgba(255, 255, 255, 0.12);
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: rotate(90deg);
}

.welcome-banner {
  background: linear-gradient(135deg, #f0fdf4 0%, #e0f2fe 100%);
  border: 1px solid #bae6fd;
}

.change-item {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
}

.change-item:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
  transform: translateX(3px);
}

.change-desc {
  line-height: 1.45;
}

/* Chip styles */
.chip-success {
  background-color: #ecfdf5 !important;
  color: #059669 !important;
  border: 1px solid #a7f3d0 !important;
}

.chip-info {
  background-color: #eff6ff !important;
  color: #2563eb !important;
  border: 1px solid #bfdbfe !important;
}

.chip-warning {
  background-color: #fffbeb !important;
  color: #d97706 !important;
  border: 1px solid #fde68a !important;
}

.chip-default {
  background-color: #f1f5f9 !important;
  color: #475569 !important;
  border: 1px solid #cbd5e1 !important;
}

.history-panels {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background-color: #f8fafc;
}

.border-bottom {
  border-bottom: 1px solid #e2e8f0;
}

.bg-footer {
  background-color: #f8fafc;
}

.primary-btn-glow {
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3) !important;
  transition: all 0.2s ease;
}

.primary-btn-glow:hover {
  box-shadow: 0 6px 16px rgba(37, 99, 235, 0.45) !important;
  transform: translateY(-1px);
}
</style>
